import type { ComputedLabel } from "../../../../services/computedTraits/computedTraitsEndpoints.interface";
import type {
  HealthScoreModel,
  HealthScoreSettings,
} from "../../../../services/healthScoreSettings/healthScoreSettingsEndpoints.interface";
import type { BusinessContext } from "../../../../types/businessContext";
import {
  GENERAL_HEALTH_MODEL_SCOPE,
  HEALTH_SCORE_SCHEDULE_PRESETS,
  getConfiguredHealthModels,
  getHealthModelDefaultTitle,
  type HealthModelScopeDescriptor,
} from "./healthScoreHelpers";

export type HealthModelScopeOption = HealthModelScopeDescriptor & {
  source_hint: string;
  savedModel?: HealthScoreModel;
  unavailable?: boolean;
  segment_filter?: Record<string, any> | null;
  segment_description?: string | null;
};

export const slugifyHealthValue = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

export const formatHealthTimestamp = (value?: string | null) => {
  if (!value) return "Never";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Never";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
};

export const buildDefaultHealthScoreSettings = (): HealthScoreSettings => ({
  version: 2,
  models: [],
  schedule_enabled: false,
  run_schedule: HEALTH_SCORE_SCHEDULE_PRESETS[0].cron,
  run_schedule_preset: HEALTH_SCORE_SCHEDULE_PRESETS[0].value,
  linked_skill_id: null,
  linked_automation_id: null,
  last_synced_at: null,
  updated_at: null,
});

export const createHealthModelFromScope = (
  scope: HealthModelScopeDescriptor & { segment_filter?: Record<string, any> | null },
): HealthScoreModel => ({
  id: scope.scope_key,
  scope_type: scope.scope_type,
  scope_key: scope.scope_key,
  scope_label: scope.scope_label,
  scope_value: scope.scope_value,
  scope_source_key: scope.scope_source_key,
  scope_source_label: scope.scope_source_label,
  match_field: scope.match_field?.trim() || null,
  segment_filter: scope.segment_filter || null,
  document_title: getHealthModelDefaultTitle(scope),
  document_content: "",
  last_generated_at: null,
  updated_at: null,
});

export const hydrateHealthModel = (model: HealthScoreModel): HealthScoreModel => ({
  ...model,
  id: model.id || model.scope_key,
  scope_label: model.scope_label?.trim() || model.scope_value,
  scope_value: model.scope_value?.trim() || model.scope_label,
  scope_source_key: model.scope_source_key?.trim() || model.scope_type,
  scope_source_label: model.scope_source_label?.trim()
    || (model.scope_type === "general"
      ? "General"
      : model.scope_type === "segment"
        ? "Segment"
        : "Lifecycle stage"),
  match_field: model.match_field?.trim() || null,
  document_title: model.document_title || getHealthModelDefaultTitle(model),
  document_content: model.document_content || "",
  last_generated_at: model.last_generated_at || null,
  updated_at: model.updated_at || null,
});

export const hydrateHealthScoreSettings = (
  settings?: HealthScoreSettings | null,
): HealthScoreSettings => {
  const base = settings || buildDefaultHealthScoreSettings();
  return {
    ...buildDefaultHealthScoreSettings(),
    ...base,
    models: (base.models || []).map(hydrateHealthModel),
  };
};

export const normalizeHealthModel = (model: HealthScoreModel): HealthScoreModel => {
  const hydrated = hydrateHealthModel(model);
  return {
    ...hydrated,
    id: hydrated.id.trim(),
    scope_key: hydrated.scope_key.trim(),
    scope_label: hydrated.scope_label.trim(),
    scope_value: hydrated.scope_value.trim(),
    scope_source_key: hydrated.scope_source_key.trim(),
    scope_source_label: hydrated.scope_source_label.trim(),
    match_field: hydrated.match_field?.trim() || null,
    document_title: hydrated.document_title.trim() || getHealthModelDefaultTitle(hydrated),
    document_content: hydrated.document_content.trim(),
  };
};

export const normalizeHealthScoreSettings = (
  settings: HealthScoreSettings,
): HealthScoreSettings => ({
  ...buildDefaultHealthScoreSettings(),
  ...settings,
  run_schedule: settings.run_schedule.trim(),
  models: (settings.models || [])
    .map(normalizeHealthModel)
    .filter((model) => model.document_content.length > 0)
    .sort((left, right) => left.scope_key.localeCompare(right.scope_key)),
});

const findLifecycleStageLabel = (labels: ComputedLabel[]) =>
  labels.find((label) =>
    label.entity_type === "accounts"
    && (label.name === "lifecycle_stage"
      || label.display_name?.toLowerCase() === "lifecycle stage"));

const buildLifecycleStageScopes = (
  businessContext?: BusinessContext | null,
  labels: ComputedLabel[] = [],
) => {
  const lifecycleLabel = findLifecycleStageLabel(labels);
  const stages = (businessContext?.lifecycle_stages || [])
    .map((stage) => stage.name?.trim())
    .filter((value): value is string => Boolean(value));

  if (stages.length) {
    return stages.map((stageName) => ({
      scope_type: "lifecycle_stage" as const,
      scope_key: `lifecycle_stage:${slugifyHealthValue(stageName)}`,
      scope_label: stageName,
      scope_value: stageName,
      scope_source_key: lifecycleLabel?.name || "lifecycle_stage",
      scope_source_label: "Lifecycle stage",
      match_field: lifecycleLabel?.column_name || null,
      source_hint: "Business context",
    }));
  }

  if (!lifecycleLabel) {
    return [];
  }

  return (lifecycleLabel.label_options || []).map((option) => ({
    scope_type: "lifecycle_stage" as const,
    scope_key: `lifecycle_stage:${slugifyHealthValue(option.value)}`,
    scope_label: option.display_name || option.value,
    scope_value: option.value,
    scope_source_key: lifecycleLabel.name,
    scope_source_label: lifecycleLabel.display_name || "Lifecycle stage",
    match_field: lifecycleLabel.column_name || null,
    source_hint: "Computed label",
  }));
};

const buildSegmentScopes = (labels: ComputedLabel[] = []) =>
  labels
    .filter((label) =>
      label.entity_type === "accounts"
      && label.category === "segmentation"
      && label.name !== "lifecycle_stage"
      && Array.isArray(label.label_options)
      && label.label_options.length > 0)
    .flatMap((label) => label.label_options.map((option) => ({
      scope_type: "segment" as const,
      scope_key: `segment:${label.name}:${slugifyHealthValue(option.value)}`,
      scope_label: option.display_name || option.value,
      scope_value: option.value,
      scope_source_key: label.name,
      scope_source_label: label.display_name || "Segment",
      match_field: label.column_name || null,
      source_hint: "Computed label",
    })));

const buildBusinessContextSegmentScopes = (
  businessContext?: BusinessContext | null,
) =>
  (businessContext?.segments || [])
    .filter((segment) => segment.name?.trim())
    .map((segment) => ({
      scope_type: "segment" as const,
      scope_key: `segment:bc:${slugifyHealthValue(segment.name!)}`,
      scope_label: segment.name!.trim(),
      scope_value: segment.name!.trim(),
      scope_source_key: `bc_segment_${segment.id || slugifyHealthValue(segment.name!)}`,
      scope_source_label: "Segment",
      match_field: null,
      source_hint: "Business context",
      segment_filter: segment.fit_filter || null,
      segment_description: segment.description?.trim() || null,
    }));

export const buildAvailableHealthScopes = ({
  businessContext,
  labels,
  savedModels,
}: {
  businessContext?: BusinessContext | null;
  labels?: ComputedLabel[];
  savedModels: HealthScoreModel[];
}) => {
  const discovered = [
    {
      ...GENERAL_HEALTH_MODEL_SCOPE,
      source_hint: "Workspace fallback",
    },
    ...buildBusinessContextSegmentScopes(businessContext),
  ];

  const map = new Map<string, HealthModelScopeOption>();
  discovered.forEach((scope) => {
    map.set(scope.scope_key, scope);
  });

  savedModels.forEach((model) => {
    if (!map.has(model.scope_key)) {
      map.set(model.scope_key, {
        scope_type: model.scope_type,
        scope_key: model.scope_key,
        scope_label: model.scope_label,
        scope_value: model.scope_value,
        scope_source_key: model.scope_source_key,
        scope_source_label: model.scope_source_label,
        match_field: model.match_field || null,
        source_hint: "Saved model",
        unavailable: true,
      });
    }
  });

  return Array.from(map.values())
    .map((scope) => ({
      ...scope,
      savedModel: savedModels.find((model) => model.scope_key === scope.scope_key),
    }))
    .sort((left, right) => {
      if (left.scope_type === "general" && right.scope_type !== "general") {
        return -1;
      }
      if (right.scope_type === "general" && left.scope_type !== "general") {
        return 1;
      }
      if (left.scope_type !== right.scope_type) {
        return left.scope_type === "lifecycle_stage" ? -1 : 1;
      }
      if (left.scope_source_label !== right.scope_source_label) {
        return left.scope_source_label.localeCompare(right.scope_source_label);
      }
      return left.scope_label.localeCompare(right.scope_label);
    });
};

export const hasUnsyncedHealthSettings = (settings: HealthScoreSettings) => {
  const configuredModels = getConfiguredHealthModels(settings);
  if (!configuredModels.length) return false;

  const lastSyncedMs = settings.last_synced_at
    ? Date.parse(settings.last_synced_at)
    : Number.NaN;

  if (Number.isNaN(lastSyncedMs)) {
    return true;
  }

  const settingsUpdatedMs = settings.updated_at
    ? Date.parse(settings.updated_at)
    : Number.NaN;

  if (!Number.isNaN(settingsUpdatedMs) && settingsUpdatedMs > lastSyncedMs) {
    return true;
  }

  return configuredModels.some((model) => {
    const updatedMs = model.updated_at ? Date.parse(model.updated_at) : Number.NaN;
    return !Number.isNaN(updatedMs) && updatedMs > lastSyncedMs;
  });
};

export const encodeHealthScopeKeyForRoute = (scopeKey: string) =>
  encodeURIComponent(scopeKey);

export const decodeHealthScopeKeyFromRoute = (scopeKey?: string | null) =>
  scopeKey ? decodeURIComponent(scopeKey) : "";
